import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, getToken } from '../api/client';
import { DezoomDots } from '../components/DezoomDots';
import { GuessForm } from '../components/GuessForm';
import { ZoomImage } from '../components/ZoomImage';
import { useAuth } from '../hooks/useAuth';
import { preloadImages } from '../utils/imageCache';

import type { PhotoAttributionData } from '../components/PhotoAttribution';

interface TodayResponse {
  puzzle: { id: string; puzzleNumber: number; attribution?: PhotoAttributionData | null };
  imageUrl: string;
  zoomLevels: { level: number; imageUrl: string }[];
  currentZoomLevel: number;
  run: { id: string; status: string; dezoomsUsed?: number; currentZoomLevel?: number } | null;
  hasOfficialRun: boolean;
}

interface GuessResponse {
  correct: boolean;
  status: string;
  dezoomsUsed: number;
  currentZoomLevel: number;
  score: number | null;
  shareText: string | null;
  durationSec: number | null;
  puzzleId?: string;
  answer?: string;
  imageUrl?: string;
}

export function PlayPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, guest, loginGuest, loading: authLoading } = useAuth();
  const [today, setToday] = useState<TodayResponse | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState(0);
  const [zoomLevels, setZoomLevels] = useState<{ level: number; imageUrl: string }[]>([]);
  const [dezooms, setDezooms] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [timerSec, setTimerSec] = useState(0);

  const init = useCallback(async () => {
    if (params.get('guest') === '1' && !user && !guest && !getToken()) {
      await loginGuest();
    }
    const data = await api<TodayResponse>('/puzzles/today');
    const levels = data.zoomLevels || [];
    const level = data.run?.currentZoomLevel ?? data.currentZoomLevel;
    setToday(data);
    setZoomLevels(levels);
    setZoomLevel(level);
    preloadImages(levels.map((z) => z.imageUrl));

    if (data.run) {
      setDezooms(data.run.dezoomsUsed ?? 0);
      if (data.run.status !== 'in_progress') {
        navigate('/result', { state: { fromRun: data.run.id } });
        return;
      }
      setRunId(data.run.id);
    }
  }, [params, user, guest, loginGuest, navigate]);

  useEffect(() => {
    if (authLoading) return;
    init().catch((e) => setError(e.message));
  }, [authLoading, init]);

  useEffect(() => {
    if (!startedAt) return;
    const id = setInterval(() => {
      setTimerSec(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  const ensureRun = async () => {
    if (runId) return runId;
    const res = await api<{ runId: string; startedAt: string }>('/runs/start', { method: 'POST' });
    setRunId(res.runId);
    const t = new Date(res.startedAt).getTime();
    setStartedAt(t);
    setTimerSec(0);
    return res.runId;
  };

  const handleGuess = async (guess: string): Promise<boolean | void> => {
    setBusy(true);
    setError('');
    try {
      const id = await ensureRun();
      const res = await api<GuessResponse>(`/runs/${id}/guess`, {
        method: 'POST',
        body: JSON.stringify({ guess }),
      });

      setDezooms(res.dezoomsUsed);
      setZoomLevel(res.currentZoomLevel);

      if (res.status !== 'in_progress') {
        navigate('/result', {
          state: {
            won: res.status === 'won',
            score: res.score ?? 0,
            dezoomsUsed: res.dezoomsUsed,
            durationSec: res.durationSec ?? 0,
            shareText: res.shareText,
            puzzleNumber: today?.puzzle.puzzleNumber ?? 0,
            puzzleId: today?.puzzle.id,
            answer: res.answer,
            imageUrl: res.imageUrl,
          },
        });
        return;
      }
      return res.correct;
    } catch (e: unknown) {
      const err = e as { data?: { error?: string } };
      setError(err.data?.error || 'Erreur');
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const formatTimer = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (!today) {
    return <main className="page play"><p className="loading">{error || 'Chargement…'}</p></main>;
  }

  return (
    <main className="page play">
      <header className="play-header">
        <div className="play-header-left">
          <span className="puzzle-num">#{today.puzzle.puzzleNumber}</span>
          {startedAt != null && (
            <span className="play-timer" aria-live="polite">{formatTimer(timerSec)}</span>
          )}
        </div>
        <DezoomDots used={dezooms} />
      </header>

      <ZoomImage levels={zoomLevels} activeLevel={zoomLevel} attribution={today.puzzle.attribution} />

      <footer className="play-footer">
        {error && <p className="play-error" role="alert">{error}</p>}
        <GuessForm onSubmit={handleGuess} disabled={busy} />
      </footer>
    </main>
  );
}
